import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Offcanvas } from "react-bootstrap";
import { Moment } from "moment";

import Tooltip from "src/components/tooltip";
import InfiniteScroll from "src/components/infiniteScroll";
import DownloadReports from "../downloadReports";
import { Filter } from "../entityFilters";
import Text from "src/components/text";

import {
  fetchDiscoveryContentMatrix,
  fetchDiscoveryContentMatrxAssets,
  intializeDownloadReport,
  invalidateDiscoveryContentMatrixAssets,
  invalidateReportsDownloadData,
} from "src/redux/actions/reports";
import { CONTENT_DISCOVERY_MATRIX } from "src/constants/reports";
import userRoleService from "src/utils/userRoles.service";
import { formatTimespent } from "src/services/reports.service";

import _ from "lodash";
import cx from "classnames";
import styles from "./contentDiscoveryMatrix.module.scss";
import { EntitiesFilterTooltip } from "../entitiesFilterTooltip";
import { DiscoveryTables, EngagementTables, PerformanceTables, ReportTabs } from "src/types/reports";

interface contentDiscoveryProps {
  reportDate?: ReportDate;
  filters: Filter[];
  interval?: string;
  tabName: ReportTabs;
  tableName: DiscoveryTables | EngagementTables | PerformanceTables;
}

interface ReportDate {
  startDate: Moment;
  endDate: Moment;
  type?: string;
}

interface DiscoveryCategory {
  lower: number;
  upper: number | null;
  count: number;
}

interface ContentMatrixItem {
  low: DiscoveryCategory;
  belowAverage: DiscoveryCategory;
  average: DiscoveryCategory;
  high: DiscoveryCategory;
}

interface PaginationState {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecordsCount: number;
}
interface CategoryDetail {
  title: string;
  description: string;
}

const DefaultAssetIcon = "/images/defaultIcons/default_asset.jpg";

const ContentDiscoveryMatrix: React.FC<contentDiscoveryProps> = ({
  reportDate,
  filters,
  interval,
  tabName,
  tableName,
}) => {
  const dispatch = useDispatch();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [openScrollList, setOpenScrollList] = useState(false);
  const [selectedMatrixKey, setSelectedMatrixKey] = useState<
    keyof ContentMatrixItem | null
  >(null);

  const { discovery } = useSelector((state: any) => ({
    discovery: _.get(state, "reports.discovery", {}),
  }));

  const { download } = useSelector((state: any) => ({
    download: _.get(state, "reports.download", {}),
  }));

  const { contentMatrix, contentMatrixAssets } = discovery;

  const fetchState = {
    loading: download.loading,
    succeeded: download.succeeded,
    error: download.error,
  };

  const [activeDownloadConfig, setActiveDownloadConfig] = useState<
    typeof downloadConfig | null
  >(null);

  const handleDownloadClick = (
    key: keyof ContentMatrixItem | null,
    matrixItems: ContentMatrixItem | null,
  ) => {
    if (!downloadConfig || !key || !matrixItems) return;

    const request = createMatrixFilter(key, matrixItems);
    if (!request) return;

    dispatch(
      intializeDownloadReport({
        id: downloadConfig.name,
        request,
        page: 0,
        limit: 50,
      }),
    );

    setActiveDownloadConfig(downloadConfig);
  };

  const invalidateDownloadData = () => {
    setActiveDownloadConfig(null);
    dispatch(invalidateReportsDownloadData())
  };

  const items: ContentMatrixItem =
    contentMatrix?.item ?? ({} as ContentMatrixItem);

  const totalCount = Object.values(items).reduce(
    (sum, item) => sum + (item.count ?? 0),
    0,
  );

  const title = `Content Discovery Matrix (${totalCount})`;

  const description =
    "Categorize assets by the number of unique platform users who have viewed them. A higher count indicates greater discovery, while a low count indicates lower discovery.";

  
// This function handles the click on a matrix card and fetches the assets based on the selected category.
  const handleMatrixCardClick = (
    key: keyof ContentMatrixItem,
    matrixItems: ContentMatrixItem,
  ) => {
    if (!matrixItems || !matrixItems[key] || !reportDate) {
      return;
    }

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    const { lower, upper } = matrixItems[key];
    const filter = {
      fromViews: lower,
      toViews: upper,
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      interval: interval || "day",
      sortBy: "views",
      userType: "all",
      filters: filters || [],
    };
    setSelectedMatrixKey(key);
    dispatch(fetchDiscoveryContentMatrxAssets({ page: 0, limit: 20, filter }));
  };

  const baseFilters = useMemo(() => {
    if (!reportDate || !reportDate.startDate || !reportDate.endDate)
      return null;

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    return {
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      filters: filters || [],
      sortBy: "views",
      userType: "all",
      interval: interval || "day",
    };
  }, [reportDate, JSON.stringify(filters),interval]);

// This function creates a filter object based on the selected matrix key and the items in the content matrix.
  const createMatrixFilter = (
    key: keyof ContentMatrixItem,
    matrixItems: ContentMatrixItem,
  ) => {
    if (!matrixItems || !matrixItems[key] || !reportDate) return null;

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    const { lower, upper } = matrixItems[key];
    return {
      fromViews: lower,
      toViews: upper,
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      interval: interval || "day",
      sortBy: "views",
      userType: "all",
      filters: filters || [],
    };
  };

  useEffect(() => {
    dispatch(
      fetchDiscoveryContentMatrix({
        filter: baseFilters,
      }),
    );
  }, [reportDate, JSON.stringify(filters)]);

// This function handles loading more content in the content matrix based on the selected matrix key and base filters.
  const handleLoadMoreContentMatrix = () => {
    if (!baseFilters) return;
    if (!selectedMatrixKey) return;

    const categoryData = items[selectedMatrixKey];
    if (!categoryData) return;

    const { lower, upper } = categoryData;
    const filter = {
      ...baseFilters,
      fromViews: lower,
      toViews: upper,
      interval: interval || "day",
    };

    if (
      contentMatrixAssets.pagination.currentPage <
      contentMatrixAssets.pagination.totalPages
    ) {
      dispatch(
        fetchDiscoveryContentMatrxAssets({
          page: contentMatrixAssets.pagination.currentPage + 1,
          filter,
          limit: 20,
        }),
      );
    }
  };

  const categoryDetails: Record<string, CategoryDetail> = {
    low: {
      title: "Low Discovery",
      description: "Assets with a low number of unique viewers.",
    },
    belowAverage: {
      title: "Below Average Discovery",
      description: "Assets viewed by fewer than average unique viewers.",
    },
    average: {
      title: "Average Discovery",
      description:
        "Assets attracting an average number of unique viewers, suggesting typical interest and engagement.",
    },
    high: {
      title: "High Discovery",
      description:
        "Assets with a high number of unique viewers , indicating widespread interest and visibility.",
    },
  };

  // This section prepares the download configuration based on the selected category.
  let downloadConfig;

  if (selectedCategory && categoryDetails[selectedCategory]) {
    downloadConfig = {
      name: "discovery_content_matrix",
      title: categoryDetails[selectedCategory].title,
      fileName: `Content_Discovery_${selectedCategory}.xlsx`,
      schema: CONTENT_DISCOVERY_MATRIX,
      data: download?.items || [],
    };
  }

  // This section creates a dynamic filter based on the selected matrix key and items for the download reports component.
  const dynamicFilter = useMemo(() => {
    if (!selectedMatrixKey) return null;
    return createMatrixFilter(selectedMatrixKey, items);
  }, [selectedMatrixKey, items]);
  

  const renderScrollItem = ({ item }: { item: any }) => {
    const { asset, analytics } = item;
    return (
      <div key={asset.id} className={styles.tableRow}>
        <div className={styles.tableDataContainer}>
          <img
            src={asset.icon?.thumbnail || DefaultAssetIcon}
            alt={asset.name}
            className={styles.thumbnailIcon}
          />
          <a
            href={`/assets/${asset.id}?source=reports`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.linkWrapper}
          >
            <div className={styles.rightData}>
              <div className={styles.tableDataTitle}>
                <Text lines={1} className={styles.name} text={asset.name || ""} />
              </div>
              <span className={styles.metricsData}>
                <span className={styles.name}>
                  Added:{" "}
                  {new Date(asset.createdDate).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <span className={styles.name}>
                  {" "}
                  Author:{" "}
                  <Text text = {typeof asset.author === "object"
                    ? asset.author.firstName + " " + asset.author.lastName
                    : asset.author || "Unknown"} lines={1} className={styles.author}/>
                </span>
              </span>
            </div>
          </a>
        </div>
        <div className={styles.analyticsContainer}>
          <div className={styles.tableDataViews}>{analytics.users}</div>
          <div className={styles.tableDataViews}>{analytics.views}</div>
          <div className={styles.tableDataTime}>
            {formatTimespent(analytics.avgTimeSpent || 0)}
          </div>
        </div>
      </div>
    );
  };

  const handleCardClick = (category) => {
    setSelectedCategory(category);
    handleMatrixCardClick(category as keyof ContentMatrixItem, items);
    setOpenScrollList(true);
  };

  const handleCancelClick = () => {
    setOpenScrollList(false);
    dispatch(invalidateDiscoveryContentMatrixAssets());
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.report}>
          <div className={styles.content}>
            <div className={styles.textContainer}>
              <div className={styles.title}>{title}</div>
              <Tooltip placement="top" content={description}>
                <i className={cx(styles.paperfliteInfo, "paperflite-info")}></i>
              </Tooltip>
            </div>
            <EntitiesFilterTooltip tabName={tabName} tableName={tableName} />
          </div>
          <div className={styles.contentDiscoveryMatrix}>
            <ContentDiscoveryCard
              heading={`${(((items && items.low && items.low.count ? items.low.count : 0) / (totalCount || 1)) * 100).toFixed(1)}%`}
              title={`${items && items.low && items.low.count ? items.low.count : 0} Assets`}
              subtitle="Low discovery"
              onClick={() => handleCardClick("low")}
            />
            <ContentDiscoveryCard
              heading={`${(((items && items.belowAverage && items.belowAverage.count ? items.belowAverage.count : 0) / (totalCount || 1)) * 100).toFixed(1)}%`}
              title={`${items && items.belowAverage && items.belowAverage.count ? items.belowAverage.count : 0} Assets`}
              subtitle="Below Average discovery"
              onClick={() => handleCardClick("belowAverage")}
            />
            <ContentDiscoveryCard
              heading={`${(((items && items.average && items.average.count ? items.average.count : 0) / (totalCount || 1)) * 100).toFixed(1)}%`}
              title={`${items && items.average && items.average.count ? items.average.count : 0} Assets`}
              subtitle="Average discovery"
              onClick={() => handleCardClick("average")}
            />
            <ContentDiscoveryCard
              heading={`${(((items && items.high && items.high.count ? items.high.count : 0) / (totalCount || 1)) * 100).toFixed(1)}%`}
              title={`${items && items.high && items.high.count ? items.high.count : 0} Assets`}
              subtitle="High discovery"
              onClick={() => handleCardClick("high")}
            />
            {selectedCategory && openScrollList && (
              <ContentDiscoveryInsightsView
                title={categoryDetails[selectedCategory].title}
                show={openScrollList}
                description={categoryDetails[selectedCategory].description}
                items={contentMatrixAssets.items}
                onClose={handleCancelClick}
                pagination={contentMatrixAssets.pagination}
                loadMore={handleLoadMoreContentMatrix}
                loading={contentMatrixAssets.loading}
                renderItem={renderScrollItem}
                columns={["# of Unique Users", "Views", "Avg Time"]}
                downloadConfig={downloadConfig}
                fetchState={fetchState}
                invalidateDownloadData={invalidateDownloadData}
                downloadClick={() =>
                  handleDownloadClick(selectedMatrixKey, items)
                }
                downloadPagination={download.pagination}
                dynamicFilter={dynamicFilter}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentDiscoveryMatrix;

interface ContentDiscoveryInsightsViewProps {
  title: string;
  description?: string;
  items: any[];
  pagination: PaginationState;
  loading: boolean;
  loadMore: () => void;
  onClose: () => void;
  renderItem: any;
  columns?: string[];
  scrollItems?: any;
  show?: boolean;
  downloadConfig?: downloadConfig;
  fetchState: {
    loading: boolean;
    succeeded: boolean;
    error: any;
  };
  invalidateDownloadData: () => void;
  downloadClick: () => void;
  downloadPagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  dynamicFilter?: any;
}
interface downloadConfig {
  name: string;
  schema: any;
  fileName: string;
  title: string;
  data: any[];
}

const ContentDiscoveryInsightsView: React.FC<ContentDiscoveryInsightsViewProps> = ({
  title,
  description,
  items,
  pagination,
  loadMore,
  onClose,
  loading,
  renderItem,
  columns,
  show,
  downloadConfig,
  fetchState,
  invalidateDownloadData,
  downloadClick,
  downloadPagination,
  dynamicFilter
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const dispatch = useDispatch();

  const handleClose = () => {
    setIsClosing(true);
  };

  const handleAnimationEnd = () => {
    if (isClosing) {
      onClose();
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      backdrop={true}
      scroll={false}
      className={cx(styles.customOffcanvas)}
    >
      <div
        className={cx(styles.scrollList, { [styles.slideOut]: isClosing })}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className={styles.header}>
          <div className={styles.heading}>
            {title}
            <Tooltip placement="right" content={description}>
              <i className={cx(styles.paperfliteInfoList, "paperflite-info")} />
            </Tooltip>
          </div>
          <div className={styles.downloadContainer}>
            <div className={styles.downloadText}>
              {userRoleService.canDownloadReport() && downloadConfig && (
                <DownloadReports
                  title={downloadConfig.title}
                  fileName={downloadConfig.fileName}
                  schema={downloadConfig.schema}
                  data={downloadConfig.data || []}
                  fetchState={fetchState}
                  fetchDataForPage={(pageNumber) => {
                    dispatch(
                      intializeDownloadReport({
                        id: _.get(downloadConfig,"name",""),
                        request: dynamicFilter,
                        page: pageNumber,
                        limit: 50,
                      }),
                    );
                  }}
                  pagination={downloadPagination || {}}
                  invalidateData={invalidateDownloadData}
                  downloadClick={downloadClick}
                />
              )}
            </div>
            <div className={styles.closeContainer} onClick={handleClose}>
              <i className="paperflite-close" />
            </div>
          </div>
        </div>
        <div className={styles.tableList}>
          <div className={styles.tableHeader}>
            <div className={styles.tabletitle}>Assets</div>
            <div className={styles.tableHead}>
              {columns?.map((column, index) => (
                <div key={index} className={styles.tableHeading}>
                  {column}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.scrollBody}>
            <InfiniteScroll
              className={styles.scroll}
              loading={loading}
              hasMore={pagination.currentPage < pagination.totalPages}
              loadMore={loadMore}
              loader={() => (
                <div className={styles.loader}>loading more items...</div>
              )}
            >
              {(items || []).map((item, index) => (
                <div key={index}>{renderItem({ item })}</div>
              ))}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    </Offcanvas>
  );
};

interface contentDiscoveryCardProps {
  heading: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}

const ContentDiscoveryCard: React.FC<contentDiscoveryCardProps> = ({
  heading,
  title,
  subtitle,
  onClick,
}) => {
  return (
    <div className={styles.matrixContainer} onClick={onClick}>
      <h3 className={styles.header}>{heading}</h3>
      <div className={styles.metadata}>{title}</div>
      <div className={styles.subtitle}>{subtitle}</div>
    </div>
  );
};
